/**
 * @module Model
 */

import { CompanyMemberRequestStatus, CompanyMemberRole, CompanyType } from '@feature/companies/shared';

/**
 * Class representing a reference between the company members.
 *
 * This class encapsulates various properties related to a company member, including their
 * personal information, associated company details, and membership status.
 *
 * @class
 * @model
 */
export class CompanyMemberRef {
	/**
	 * The unique identifier for the company member reference.
	 * @type {number}
	 */
	id: number;

	/**
	 * A unique key associated with the request for this company member.
	 * @type {string}
	 */
	requestKey: string;

	/**
	 * The unique identifier for the associated company.
	 * @type {number}
	 */
	companyId: number;

	/**
	 * The name of the associated company.
	 * @type {string}
	 */
	companyName: string;

	/**
	 * The unique identifier for the owner of the company.
	 * @type {number}
	 */
	companyOwnerId: number;

	/**
	 * The email address of the owner of the company.
	 * @type {string}
	 */
	companyOwnerEmail: string;

	/**
	 * The unique identifier for the company profile.
	 * @type {number}
	 */
	companyProfileId: number;

	/**
	 * Optional property indicating the signup step the editing
	 * of the company profile was left on.
	 * @type {string | undefined}
	 */
	companyProfileSignupStep?: string;

	/**
	 * The type of the company.
	 * @type {CompanyType}
	 */
	companyType: CompanyType;

	/**
	 * Indicates whether the company is classified as an enterprise.
	 * @type {boolean}
	 */
	companyIsEnterprise: boolean;

	/**
	 * Indicates the preference that controls the display options for non-performance payment.
	 * @type {boolean}
	 */
	companyCanBePaidAsVendor?: boolean;

	/**
	 * Indicates the preference that controls the access to non-performance payment.
	 * @type {boolean}
	 */
	companyCanPayOrRequestAsVendor?: boolean;

	/**
	 * The unique identifier for the sender of the request.
	 * @type {number}
	 */
	senderId: number;

	/**
	 * The first name of the sender.
	 * @type {string}
	 */
	senderFirstName: string;

	/**
	 * The middle name of the sender (optional).
	 * @type {string}
	 */
	senderMiddleName: string;

	/**
	 * The last name of the sender.
	 * @type {string}
	 */
	senderLastName: string;

	/**
	 * The full name of the sender.
	 * @type {string}
	 */
	senderFullName: string;

	/**
	 * The email address of the sender.
	 * @type {string}
	 */
	senderEmail: string;

	/**
	 * Optional property indicating the unique identifier for the invited member.
	 * @type {number | undefined}
	 */
	memberId?: number;

	/**
	 * The first name of the member.
	 * @type {string}
	 */
	memberFirstName: string;

	/**
	 * The middle name of the member (optional).
	 * @type {string}
	 */
	memberMiddleName: string;

	/**
	 * The last name of the member.
	 * @type {string}
	 */
	memberLastName: string;

	/**
	 * The full name of the member.
	 * @type {string}
	 */
	memberFullName: string;

	/**
	 * The email address of the member.
	 * @type {string}
	 */
	memberEmail: string;

	/**
	 * Indicates whether the member user is new.
	 * @type {boolean}
	 */
	memberIsNew: boolean;

	/**
	 * The role of the member within the company.
	 * @type {CompanyMemberRole}
	 */
	memberRole: CompanyMemberRole;

	/**
	 * The status of the company member request.
	 * @type {CompanyMemberRequestStatus}
	 */
	status: CompanyMemberRequestStatus;

	/**
	 * Indicates whether the initial popup has been shown.
	 * @type {boolean}
	 */
	initialPopup: boolean;

	/**
	 * The date when the member reference was last updated.
	 * @type {Date}
	 */
	updatedDate: Date;

	/**
	 * Getter that indicates whether the company type is classified as an agent.
	 *
	 * @getter
	 * @returns {boolean} True if the company type is 'agent', otherwise false.
	 */
	get isAgent(): boolean {
		return this.companyType !== undefined && this.companyType?.toString() === CompanyType[CompanyType.agent];
	}

	/**
	 * Getter that indicates whether the company type is classified as a customer.
	 *
	 * @getter
	 * @returns {boolean} True if the company type is 'customer', otherwise false.
	 */
	get isCustomer(): boolean {
		return this.companyType !== undefined && this.companyType?.toString() === CompanyType[CompanyType.customer];
	}

	/**
	 * Getter that indicates the signup step the editing of the company profile was left on.
	 *
	 * @getter
	 * @returns {string | undefined} The signup step for the company profile or undefined if not set.
	 */
	get signupStep(): string {
		return this.companyProfileSignupStep;
	}
}
